import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    TrialItemTemplateCategoryBreadcrumbs,
    TrialItemTemplateCategoryCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { SettingsTrialSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as CategoriesActions from "../../../actions/categories";
import * as NavigationActions from "../../../actions/navigation";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { TrialLookupsLayout } from "../../index";

import "./TrialItemTemplateCategoryPage.scss";

interface TrialItemTemplateCategoryPageProps {
    form: Dtos.Category & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    trialItemTemplateCategory: Dtos.Category;
    lookups: Dtos.Lookup[];
    loadingTrialItemTemplateCategory: boolean;
    loadTrialItemTemplateCategoryFailure: boolean;
    loadTrialItemTemplateCategoryErrorCode: string;
    savingTrialItemTemplateCategory: boolean;
    saveTrialItemTemplateCategorySuccess: boolean;
    saveTrialItemTemplateCategoryFailure: boolean;
    creatingTrialItemTemplateCategory: boolean;
    createTrialItemTemplateCategorySuccess: boolean;
    validatingTrialItemTemplateCategory: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearTrialItemTemplateCategory: CategoriesActions.ICategoryClearActionCreator;
    loadTrialItemTemplateCategory: CategoriesActions.ICategoryLoadByIdActionCreator;
    saveTrialItemTemplateCategory: CategoriesActions.ICategorySaveActionCreator;
    createTrialItemTemplateCategory: CategoriesActions.ICategoryCreateActionCreator;
    validateTrialItemTemplateCategory: CategoriesActions.ICategoryValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.category";

const TrialItemTemplateCategoryPage: FC<TrialItemTemplateCategoryPageProps> = ({
    form,
    formState,
    formProperties,
    trialItemTemplateCategory,
    lookups,
    loadingTrialItemTemplateCategory,
    loadTrialItemTemplateCategoryFailure,
    loadTrialItemTemplateCategoryErrorCode,
    savingTrialItemTemplateCategory,
    saveTrialItemTemplateCategorySuccess,
    saveTrialItemTemplateCategoryFailure,
    creatingTrialItemTemplateCategory,
    createTrialItemTemplateCategorySuccess,
    validatingTrialItemTemplateCategory,
    validationFailures,
    permissions,
    clearTrialItemTemplateCategory,
    loadTrialItemTemplateCategory,
    saveTrialItemTemplateCategory,
    createTrialItemTemplateCategory,
    validateTrialItemTemplateCategory,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { trialItemTemplateCategoryId } = useParams<{
        trialItemTemplateCategoryId: string;
    }>();
    const trialItemTemplateCategoryIdNumber = parseInt(
        trialItemTemplateCategoryId,
        10
    );

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.Category = {} as Dtos.Category;

        loadForm(
            reduxFormName,
            Object.assign({ ...trialItemTemplateCategory }, extraData)
        );
    };


    // Load trial item template category
    useEffect(() => {
        if (
            trialItemTemplateCategoryIdNumber &&
            !trialItemTemplateCategory &&
            !loadingTrialItemTemplateCategory
        ) {
            setDrawerItems(SettingsTrialSubMenu(permissions));
            setBreadcrumbs(
                TrialItemTemplateCategoryBreadcrumbs(
                    trialItemTemplateCategoryIdNumber
                )
            );
            loadTrialItemTemplateCategory(trialItemTemplateCategoryIdNumber);
        } else if (
            !trialItemTemplateCategoryIdNumber &&
            !creatingTrialItemTemplateCategory &&
            !createTrialItemTemplateCategorySuccess
        ) {
            createTrialItemTemplateCategory();
            setBreadcrumbs(TrialItemTemplateCategoryCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (trialItemTemplateCategory && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        trialItemTemplateCategoryIdNumber,
        trialItemTemplateCategory,
        setDrawerItems,
        SettingsTrialSubMenu,
        permissions,
        setBreadcrumbs,
        TrialItemTemplateCategoryBreadcrumbs,
        loadTrialItemTemplateCategory,
        createTrialItemTemplateCategory,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingTrialItemTemplateCategory,
        creatingTrialItemTemplateCategory,
        createTrialItemTemplateCategorySuccess,
        TrialItemTemplateCategoryCreateBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialItemTemplateCategoryFailure) {
            navigate(`/error/${loadTrialItemTemplateCategoryErrorCode}`);
        }
    }, [
        loadTrialItemTemplateCategoryFailure,
        loadTrialItemTemplateCategoryErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (
            trialItemTemplateCategoryIdNumber &&
            trialItemTemplateCategory?.value
        ) {
            setBreadcrumbs(
                TrialItemTemplateCategoryBreadcrumbs(
                    trialItemTemplateCategoryIdNumber,
                    trialItemTemplateCategory?.value
                )
            );
        }
    }, [
        trialItemTemplateCategoryIdNumber,
        trialItemTemplateCategory,
        setBreadcrumbs,
        TrialItemTemplateCategoryBreadcrumbs,
    ]);

    // Reload trial item template category after saving
    useEffect(() => {
        if (
            !savingTrialItemTemplateCategory &&
            saveTrialItemTemplateCategorySuccess
        ) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Trial item template category saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = "/settings/trial/itemtemplatecategories";
                navigate(url);
            } else if (!trialItemTemplateCategoryIdNumber) {
                let url: string = `/settings/trial/itemtemplatecategories/${trialItemTemplateCategory?.id}`;
                navigate(url);
                clearTrialItemTemplateCategory();
            }
        }
    }, [
        savingTrialItemTemplateCategory,
        saveTrialItemTemplateCategorySuccess,
        trialItemTemplateCategoryIdNumber,
        navigate,
        clearTrialItemTemplateCategory,
        saveAndReturn,
        trialItemTemplateCategory,
    ]);

    // Failed saving
    useEffect(() => {
        if (
            !savingTrialItemTemplateCategory &&
            saveTrialItemTemplateCategoryFailure
        ) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingTrialItemTemplateCategory, saveTrialItemTemplateCategoryFailure]);

    // Cleanup effect to clear the trial item template category on component unmount
    useEffect(() => {
        return () => {
            clearTrialItemTemplateCategory();
            resetForm(reduxFormName);
        };
    }, [clearTrialItemTemplateCategory, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/settings/trial/itemtemplatecategories";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.Category) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.Category = {} as Dtos.Category;

            await saveTrialItemTemplateCategory(
                Object.assign({ ...data }, changes)
            );

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.Category = {} as Dtos.Category;

            if (
                form &&
                hasBeenSubmitted &&
                !validatingTrialItemTemplateCategory
            ) {
                await validateTrialItemTemplateCategory(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.CategoriesUpdate,
                Dtos.Permission.CategoriesCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTrialItemTemplateCategory ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "category",
                                        {
                                            value: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "category",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "category",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "trialItemTemplateCategory",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingTrialItemTemplateCategory,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <TrialLookupsLayout
            loading={loadingTrialItemTemplateCategory}
            permission={Dtos.Permission.CategoriesView}
        >
            {renderContent}
        </TrialLookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialItemTemplateCategoryPageProps> => {
    let trialItemTemplateCategory: Dtos.Category | undefined = !(
        state.categories.formData instanceof Array
    )
        ? state.categories.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialItemTemplateCategory: trialItemTemplateCategory!,
        loadingTrialItemTemplateCategory:
            state.categories.loadState &&
            state.categories.loadState.status == RequestState.Pending,
        loadTrialItemTemplateCategoryFailure:
            state.categories.loadState &&
            state.categories.loadState.status == RequestState.Failure,
        loadTrialItemTemplateCategoryErrorCode:
            state.categories.loadState.errorCode,
        savingTrialItemTemplateCategory:
            state.categories.saveState &&
            state.categories.saveState.status == RequestState.Pending,
        saveTrialItemTemplateCategorySuccess:
            state.categories.saveState &&
            state.categories.saveState.status == RequestState.Success,
        saveTrialItemTemplateCategoryFailure:
            state.categories.saveState &&
            state.categories.saveState.status == RequestState.Failure,
        creatingTrialItemTemplateCategory:
            state.categories.createState &&
            state.categories.createState.status == RequestState.Pending,
        createTrialItemTemplateCategorySuccess:
            state.categories.createState &&
            state.categories.createState.status == RequestState.Success,
        validatingTrialItemTemplateCategory:
            state.categories.validateState &&
            state.categories.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: [],
        form: state.reduxForms.category,
        formState: state.categories.formState,
        formProperties: state.categories.formProperties,
        validationFailures: trialItemTemplateCategory
            ? state.categories.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<TrialItemTemplateCategoryPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadTrialItemTemplateCategory: bindActionCreators(
            CategoriesActions.LoadCategoryById,
            dispatch
        ),
        clearTrialItemTemplateCategory: bindActionCreators(
            CategoriesActions.Clear,
            dispatch
        ),
        saveTrialItemTemplateCategory: bindActionCreators(
            CategoriesActions.SaveCategory,
            dispatch
        ),
        createTrialItemTemplateCategory: bindActionCreators(
            CategoriesActions.CreateCategory,
            dispatch
        ),
        validateTrialItemTemplateCategory: bindActionCreators(
            CategoriesActions.ValidateCategory,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrialItemTemplateCategoryPage);
